import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "./ui/alert-dialog";

const INITIAL_FORM_STATE = {
  Garçon: 0,
  Fille: 0,
  Handicap: 0,
  Nonpédalant: 0
};

const StudentInfoTab = ({ data, onUpdate, onTabChange, currentTab, requestedTab }) => {
  // États du formulaire
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // États de l'interface
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [error, setError] = useState(null);

  // Initialisation et mise à jour des données
  useEffect(() => {
    if (data) {
      const newFormData = {
        Garçon: data.Garçon || 0,
        Fille: data.Fille || 0,
        Handicap: data.Handicap || 0,
        Nonpédalant: data.Nonpédalant || 0
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
  }, [data]);

  // Détection des changements non sauvegardés
  useEffect(() => {
    if (initialFormData) {
      const hasChanges = Object.keys(formData).some(
        key => formData[key] !== initialFormData[key]
      );
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, initialFormData]);

  // Gestion des demandes de changement d'onglet
  useEffect(() => {
    if (requestedTab && requestedTab !== currentTab && hasUnsavedChanges) {
      setShowExitDialog(true);
    } else if (requestedTab && !hasUnsavedChanges) {
      onTabChange(requestedTab);
    }
  }, [requestedTab, currentTab, hasUnsavedChanges, onTabChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = value === '' ? '' : Math.max(0, parseInt(value) || 0);
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? 0 : Math.max(0, parseInt(value) || 0)
    }));
  };

  const showSuccessNotification = () => {
    const notification = document.createElement('div');
    notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-transform duration-500 flex items-center space-x-2';
    notification.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
      <span>Modifications enregistrées avec succès</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.transform = 'translateY(150%)';
      setTimeout(() => document.body.removeChild(notification), 500);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      const cleanedData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [key, parseInt(value) || 0])
      );
      
      await onUpdate(cleanedData);
      setInitialFormData(cleanedData);
      setHasUnsavedChanges(false);
      showSuccessNotification();
    } catch (err) {
      setError('Erreur lors de la mise à jour des informations. Veuillez réessayer.');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmTabChange = async (shouldSave) => {
    try {
      if (shouldSave) {
        await handleSubmit();
      }
      onTabChange(requestedTab);
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setShowExitDialog(false);
    }
  };

  const total = formData.Garçon + formData.Fille;

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      {/* Header avec icône */}
      <div className="flex items-center mb-6">
        <Users className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold text-gray-900">Informations des élèves</h2>
      </div>

      {/* Indicateur de modifications */}
      {hasUnsavedChanges && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-600 rounded-lg flex items-center">
          <span className="mr-2">⚠️</span>
          <span>Modifications non enregistrées</span>
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Effectifs de la classe */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Effectifs de la classe
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de garçons
                </label>
                <input
                  type="number"
                  name="Garçon"
                  value={formData.Garçon}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="0"
                  className="w-full p-2.5 border border-gray-300 rounded-lg
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-colors duration-200"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de filles
                </label>
                <input
                  type="number"
                  name="Fille"
                  value={formData.Fille}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="0"
                  className="w-full p-2.5 border border-gray-300 rounded-lg
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-colors duration-200"
                />
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-900">
                    Total d'élèves:
                  </span>
                  <span className="text-lg font-semibold text-blue-600 bg-blue-50 px-4 py-1 rounded-full">
                    {total}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Besoins spécifiques */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Besoins spécifiques
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Élèves en situation de handicap
                </label>
                <input
                  type="number"
                  name="Handicap"
                  value={formData.Handicap}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="0"
                  className="w-full p-2.5 border border-gray-300 rounded-lg
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-colors duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Élèves non pédalants
                </label>
                <input
                  type="number"
                  name="Nonpédalant"
                  value={formData.Nonpédalant}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min="0"
                  className="w-full p-2.5 border border-gray-300 rounded-lg
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bouton de soumission */}
        <div className="flex justify-end pt-6">
          <button
            type="submit"
            disabled={isSubmitting || !hasUnsavedChanges}
            className={`
              inline-flex items-center px-6 py-3 rounded-lg text-white font-medium
              transition-all duration-200
              ${(isSubmitting || !hasUnsavedChanges)
                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                : 'bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow transform hover:scale-[1.02]'}
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            `}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Enregistrement en cours...
              </>
            ) : (
              'Enregistrer les modifications'
            )}
          </button>
        </div>
      </form>

      {/* Dialog de confirmation */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter cet onglet ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="space-x-2">
            <AlertDialogCancel 
              onClick={() => setShowExitDialog(false)}
              className="hover:bg-gray-100"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(false)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Quitter sans enregistrer
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleConfirmTabChange(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Enregistrer et quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StudentInfoTab;